import { alpha, createTheme } from "@mui/material/styles";
import { srRS } from "@mui/material/locale";

const palette = {
    light: {
        primary: { main: "#1F5CD7", light: "#5C8FF0", dark: "#0E3FAD", contrastText: "#FFFFFF" },
        secondary: { main: "#0EA5E9", light: "#38BDF8", dark: "#0284C7", contrastText: "#FFFFFF" },
        success: { main: "#10B981", light: "#34D399", dark: "#059669", contrastText: "#FFFFFF" },
        warning: { main: "#F59E0B", light: "#FBBF24", dark: "#D97706", contrastText: "#1F2937" },
        error: { main: "#EF4444", light: "#F87171", dark: "#DC2626", contrastText: "#FFFFFF" },
        info: { main: "#3B82F6", light: "#60A5FA", dark: "#2563EB", contrastText: "#FFFFFF" },
        background: { default: "#F6F8FB", paper: "#FFFFFF" },
        text: { primary: "#0F172A", secondary: "#475569", disabled: "#94A3B8" },
        divider: "rgba(15, 23, 42, 0.08)",
        action: {
            active: "rgba(15, 23, 42, 0.6)",
            hover: "rgba(15, 23, 42, 0.04)",
            selected: "rgba(31, 92, 215, 0.08)",
            disabled: "rgba(15, 23, 42, 0.26)",
            disabledBackground: "rgba(15, 23, 42, 0.06)",
        },
    },
    dark: {
        primary: { main: "#7BA7F7", light: "#9DBDF9", dark: "#5C8FF0", contrastText: "#0B1220" },
        secondary: { main: "#38BDF8", light: "#7DD3FC", dark: "#0EA5E9", contrastText: "#0B1220" },
        success: { main: "#34D399", light: "#6EE7B7", dark: "#10B981", contrastText: "#0B1220" },
        warning: { main: "#FBBF24", light: "#FCD34D", dark: "#F59E0B", contrastText: "#0B1220" },
        error: { main: "#F87171", light: "#FCA5A5", dark: "#EF4444", contrastText: "#0B1220" },
        info: { main: "#60A5FA", light: "#93C5FD", dark: "#3B82F6", contrastText: "#0B1220" },
        background: { default: "#0B1220", paper: "#111A2E" },
        text: { primary: "#E2E8F0", secondary: "#94A3B8", disabled: "#475569" },
        divider: "rgba(226, 232, 240, 0.08)",
        action: {
            active: "rgba(226, 232, 240, 0.7)",
            hover: "rgba(226, 232, 240, 0.06)",
            selected: "rgba(123, 167, 247, 0.16)",
            disabled: "rgba(226, 232, 240, 0.3)",
            disabledBackground: "rgba(226, 232, 240, 0.08)",
        },
    },
};

const typography = {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: "2.5rem", lineHeight: 1.2, letterSpacing: "-0.02em" },
    h2: { fontWeight: 700, fontSize: "2rem", lineHeight: 1.25, letterSpacing: "-0.015em" },
    h3: { fontWeight: 600, fontSize: "1.5rem", lineHeight: 1.3, letterSpacing: "-0.01em" },
    h4: { fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.35, letterSpacing: "-0.005em" },
    h5: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: "1rem", lineHeight: 1.45 },
    subtitle1: { fontWeight: 500, fontSize: "1rem", lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.5 },
    body1: { fontSize: "0.95rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.55 },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: "0.01em" },
    caption: { fontSize: "0.75rem", lineHeight: 1.4 },
    overline: {
        fontWeight: 600,
        fontSize: "0.7rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        lineHeight: 1.4,
    },
};

const shape = { borderRadius: 12 };

const buildComponents = (mode) => ({
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
            },
            "*::-webkit-scrollbar": { width: 8, height: 8 },
            "*::-webkit-scrollbar-track": { background: "transparent" },
            "*::-webkit-scrollbar-thumb": {
                background:
                    mode === "dark" ? "rgba(226,232,240,0.14)" : "rgba(15,23,42,0.18)",
                borderRadius: 4,
            },
            "*::-webkit-scrollbar-thumb:hover": {
                background:
                    mode === "dark" ? "rgba(226,232,240,0.22)" : "rgba(15,23,42,0.28)",
            },
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: { backgroundImage: "none" },
        },
    },
    MuiCard: {
        styleOverrides: {
            root: {
                borderRadius: 16,
                border: `1px solid ${
                    mode === "dark" ? "rgba(226,232,240,0.08)" : "rgba(15,23,42,0.06)"
                }`,
                boxShadow: "none",
            },
        },
    },
    MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
            root: {
                borderRadius: 999,
                paddingInline: 20,
                paddingBlock: 9,
                fontWeight: 600,
            },
            sizeLarge: { paddingBlock: 12, fontSize: "0.95rem" },
            sizeSmall: { paddingInline: 12, paddingBlock: 5 },
            containedPrimary: {
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: { borderRadius: 12 },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: 12,
                backgroundColor: mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF",
            },
            notchedOutline: {
                borderColor:
                    mode === "dark" ? "rgba(226,232,240,0.16)" : "rgba(15,23,42,0.14)",
            },
        },
    },
    MuiTextField: {
        defaultProps: { variant: "outlined" },
    },
    MuiChip: {
        styleOverrides: {
            root: { fontWeight: 600, borderRadius: 8 },
            sizeSmall: { height: 24 },
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                borderRadius: 8,
                fontSize: "0.75rem",
                padding: "6px 10px",
                backgroundColor:
                    mode === "dark" ? "rgba(226,232,240,0.92)" : "rgba(15,23,42,0.92)",
                color: mode === "dark" ? "#0F172A" : "#FFFFFF",
            },
        },
    },
    MuiAppBar: {
        defaultProps: { color: "default", elevation: 0 },
    },
    MuiDivider: {
        styleOverrides: {
            root: {
                borderColor:
                    mode === "dark" ? "rgba(226,232,240,0.08)" : "rgba(15,23,42,0.06)",
            },
        },
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                borderRadius: 10,
                "&.Mui-selected": {
                    backgroundColor: alpha(palette[mode].primary.main, 0.12),
                    color: palette[mode].primary.main,
                    "& .MuiListItemIcon-root": { color: palette[mode].primary.main },
                    "&:hover": {
                        backgroundColor: alpha(palette[mode].primary.main, 0.18),
                    },
                },
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            root: { borderRadius: 12 },
        },
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                borderRadius: 12,
                border: `1px solid ${
                    mode === "dark" ? "rgba(226,232,240,0.08)" : "rgba(15,23,42,0.06)"
                }`,
                boxShadow:
                    mode === "dark"
                        ? "0 10px 40px rgba(0,0,0,0.5)"
                        : "0 10px 40px rgba(15,23,42,0.08)",
            },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                marginInline: 6,
                marginBlock: 2,
                paddingBlock: 8,
            },
        },
    },
});

export const createAppTheme = (mode = "light") =>
    createTheme(
        {
            palette: { mode, ...palette[mode] },
            typography,
            shape,
            components: buildComponents(mode),
        },
        srRS
    );

export const lightTheme = createAppTheme("light");
export const darkTheme = createAppTheme("dark");
